/**
 * ActivitiesTimeline - Timeline premium de atividades do feed global
 * Baseado em CustomerActivityTimelinePremium (CTRL+C / CTRL+V adaptado)
 * 
 * Adiciona:
 * - Link para o cliente (além do deal)
 * - Contador de comentários (futuro)
 * 
 * @version 1.0.0
 * RELEASE: 2026-02-09 v1.0.0 - Adaptado de CustomerActivityTimelinePremium
 */
import React from 'react';
import { format, isPast, isFuture } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  FileText,
  Phone,
  Mail,
  Calendar,
  CheckSquare,
  ArrowRightLeft,
  User,
  TrendingUp,
  TrendingDown,
  RefreshCw,
  Trash2,
  CheckCircle2,
  CalendarClock,
  AlertCircle,
  Plus,
  Briefcase,
  Users,
  RotateCcw,
  FileSignature,
  CreditCard
} from 'lucide-react';
import { ActivityType } from '@/hooks/useCrmDealActivities';
import { ActivityAttachmentsDisplay } from '@/components/crm/deals/ActivityAttachmentsDisplay';
import { ActivityCommentSection } from './ActivityCommentSection';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';
import { SmartAvatar } from '@/components/ui/smart-avatar';
import { cn } from '@/lib/utils';
import { formatDealCode } from '@/lib/formatters';
import { groupByDatePreserveOrder, DateGroup } from '@/lib/utils/group-by-date';
import type { FeedActivity } from '@/hooks/useActivitiesFeed';
import googleMeetIcon from '@/assets/google-meet-icon.svg';

// Props do componente
interface ActivitiesTimelineProps {
  activities: FeedActivity[];
  onComplete: (activityId: string) => void;
  onReopen: (activityId: string) => void;
  onDelete: (activityId: string) => void;
  /** v1.11.0: marca/desmarca como lido (Notas e Sistema) */
  onMarkRead?: (activityId: string) => void;
  onMarkUnread?: (activityId: string) => void;
  isCompleting: boolean;
  isReopening: boolean;
  isDeleting: boolean;
  isMarkingRead?: boolean;
  /** Callback para abrir modal de negócio */
  onOpenDeal?: (dealId: string) => void;
  /** Se true, os comentários iniciam expandidos por padrão */
  commentsDefaultExpanded?: boolean;
}

// Mapeamento de ícones por tipo de atividade
const activityIcons: Record<ActivityType, React.ComponentType<{ className?: string }>> = {
  note: FileText,
  call: Phone,
  email: Mail,
  meeting: Calendar,
  task: CheckSquare,
  phase_change: ArrowRightLeft,
  status_change: RefreshCw,
  assignment: User,
  value_change: TrendingUp,
  created: Plus,
  product_added: TrendingUp,
  product_removed: TrendingDown,
  contract_generated: FileSignature,
  charge_generated: CreditCard,
};

// Classes de gradiente por tipo para ícones
const activityGradients: Record<ActivityType, string> = {
  note: 'bg-gradient-to-br from-blue-500 to-blue-600',
  call: 'bg-gradient-to-br from-green-500 to-green-600',
  email: 'bg-gradient-to-br from-purple-500 to-purple-600',
  meeting: 'bg-gradient-to-br from-orange-500 to-orange-600',
  task: 'bg-gradient-to-br from-amber-500 to-amber-600',
  phase_change: 'bg-gradient-to-br from-slate-400 to-slate-500 dark:from-slate-600 dark:to-slate-700',
  status_change: 'bg-gradient-to-br from-cyan-500 to-cyan-600',
  assignment: 'bg-gradient-to-br from-indigo-500 to-indigo-600',
  value_change: 'bg-gradient-to-br from-emerald-500 to-emerald-600',
  created: 'bg-gradient-to-br from-slate-400 to-slate-500 dark:from-slate-600 dark:to-slate-700',
  product_added: 'bg-gradient-to-br from-teal-500 to-teal-600',
  product_removed: 'bg-gradient-to-br from-rose-500 to-rose-600',
  contract_generated: 'bg-gradient-to-br from-violet-500 to-violet-600',
  charge_generated: 'bg-gradient-to-br from-emerald-500 to-emerald-600',
};

/**
 * Componente separador de data
 */
function DateSeparator({ label }: { label: string }) {
  return (
    <div className="flex items-center justify-center my-4 pl-12">
      <div className="flex-1 h-px bg-border" />
      <span className="px-3 py-0.5 text-xs font-medium text-muted-foreground bg-muted rounded-full">
        {label}
      </span>
      <div className="flex-1 h-px bg-border" />
    </div>
  );
}

/**
 * Timeline de atividades do feed global
 */
export function ActivitiesTimeline({
  activities,
  onComplete,
  onReopen,
  onDelete,
  onMarkRead,
  onMarkUnread,
  isCompleting,
  isReopening,
  isDeleting,
  isMarkingRead,
  onOpenDeal,
  commentsDefaultExpanded = false
}: ActivitiesTimelineProps) {
  // RELEASE: 2026-02-13 - Estado para expandir/colapsar descrições longas
  const [expandedDescriptions, setExpandedDescriptions] = React.useState<Set<string>>(new Set());

  /** Alterna expansão da descrição de uma atividade */
  const toggleDescription = (id: string) => {
    setExpandedDescriptions(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // Agrupa atividades por data
  const groupedActivities = groupByDatePreserveOrder(activities);

  // Verifica se atividade tem agendamento pendente
  // Notas não têm ciclo de conclusão
  const hasSchedulePending = (activity: FeedActivity) => {
    if (activity.activity_type === 'note') return false;
    return activity.scheduled_at && !activity.completed_at;
  };

  // Verifica se agendamento está atrasado
  const isOverdue = (activity: FeedActivity) => {
    if (activity.activity_type === 'note') return false;
    if (!activity.scheduled_at || activity.completed_at) return false;
    return isPast(new Date(activity.scheduled_at));
  };

  // Verifica se agendamento é futuro
  const isUpcoming = (activity: FeedActivity) => {
    if (activity.activity_type === 'note') return false;
    if (!activity.scheduled_at || activity.completed_at) return false;
    return isFuture(new Date(activity.scheduled_at));
  };

  return (
    <TooltipProvider>
      {/* Container principal com linha conectora contínua */}
      <div className="relative">
        {/* Linha vertical contínua */}
        {activities.length > 1 && (
          <div className="absolute left-[15px] top-8 bottom-8 w-0.5 bg-border z-0" />
        )}

        <div className="space-y-1">
          {groupedActivities.map((group: DateGroup<FeedActivity>, groupIndex: number) => (
            <div key={group.dateKey}>
              {/* Separador de data */}
              <DateSeparator label={group.dateLabel} />

              {/* Cards de atividades do grupo */}
              <div className="space-y-2">
                {group.items.map((activity) => {
                  const activityType = activity.activity_type as ActivityType;
                  const Icon = activityIcons[activityType] || FileText;
                  const gradientClass = activity.is_system
                    ? 'bg-gradient-to-br from-slate-400 to-slate-500 dark:from-slate-600 dark:to-slate-700'
                    : (activityGradients[activityType] || activityGradients.note);
                  const isPending = hasSchedulePending(activity);
                  const overdue = isOverdue(activity);
                  const upcoming = isUpcoming(activity);

                  return (
                    <div
                      key={activity.id}
                      className={cn(
                        "relative bg-card rounded-xl border p-3 transition-all group ml-8",
                        "shadow-sm hover:shadow-md",
              isPending && overdue && "border-l-3 border-l-red-400",
              isPending && upcoming && "border-l-3 border-l-orange-400",
                        activity.activity_type !== 'note' && activity.completed_at && "opacity-75"
                      )}
                    >
                      {/* Ícone posicionado sobre a linha conectora */}
                      <div className={cn(
                        "absolute -left-8 top-3 h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0 text-white shadow-sm z-10",
                        gradientClass
                      )}>
                        <Icon className="h-4 w-4" />
                      </div>

                      <div className="flex items-start gap-3">
                        {/* Conteúdo principal */}
                        <div className="flex-1 min-w-0">
                          {/* LINHA 1: Deal + Título + Badges (CONSOLIDADO) */}
                          <div className="flex items-center gap-2 flex-wrap min-w-0">
                            {/* Deal clicável (se existir) */}
                            {activity.deal && (
                              <div 
                                className="flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-primary/5 border border-primary/10 cursor-pointer hover:bg-primary/10 transition-colors flex-shrink-0"
                                onClick={() => onOpenDeal?.(activity.deal!.id)}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => e.key === 'Enter' && onOpenDeal?.(activity.deal!.id)}
                              >
                                <Briefcase className="h-3.5 w-3.5 text-primary" />
                                <span className="font-mono text-xs font-semibold text-primary">
                                  {formatDealCode(activity.deal.id)}
                                </span>
                                <span className="text-muted-foreground/50">—</span>
                                <span className="text-xs text-muted-foreground truncate max-w-[120px]">
                                  {activity.deal.title}
                                </span>
                              </div>
                            )}

                            {/* Separador visual (se tem deal) */}
                            {activity.deal && (
                              <span className="text-muted-foreground/30">—</span>
                            )}

                            {/* Título da atividade */}
                            <h4 className={cn(
                              "text-sm font-semibold truncate",
                              activity.activity_type !== 'note' && activity.completed_at && "line-through text-muted-foreground"
                            )}>
                              {activity.title}
                            </h4>

                            {/* Badge de agendamento */}
                            {isPending && (
                              <Badge
                                variant="outline"
                                className={cn(
                                  "text-[10px] px-1.5 py-0.5 font-medium flex-shrink-0",
                                  overdue
                                    ? "bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-400"
                                    : "bg-orange-100 text-orange-700 border-orange-200 dark:bg-orange-900/30 dark:text-orange-400"
                                )}
                              >
                                {overdue ? (
                                  <AlertCircle className="h-3 w-3 mr-1" />
                                ) : (
                                  <CalendarClock className="h-3 w-3 mr-1" />
                                )}
                                {format(new Date(activity.scheduled_at!), "dd/MM HH:mm")}
                              </Badge>
                            )}

                            {/* Badge de sistema ou ícone Google Meet para agendamento online */}
                            {activity.is_system && (
                              activity.activity_type === 'meeting' && activity.conferencing_link ? (
                                <Tooltip>
                                  <TooltipTrigger>
                                    <img src={googleMeetIcon} alt="Google Meet" className="h-4 w-4" />
                                  </TooltipTrigger>
                                  <TooltipContent>Agendamento Online</TooltipContent>
                                </Tooltip>
                              ) : (
                                <Badge variant="secondary" className="text-[9px] px-1.5 py-0 h-4 flex-shrink-0">
                                  Sistema
                                </Badge>
                              )
                            )}

                            {/* Badge concluída */}
                            {activity.completed_at && (
                              <Badge
                                variant="outline"
                                className="text-[10px] px-1.5 py-0.5 bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 flex-shrink-0"
                              >
                                <CheckCircle2 className="h-3 w-3 mr-1" />
                                Concluída
                              </Badge>
                            )}
                          </div>

                          {/* LINHA 2: Referência ao cliente (se houver) - SEPARADA */}
                          {activity.customer && (
                            <div className="flex items-center gap-2 mt-1.5">
                              <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-muted/50 border border-border/50">
                                <Users className="h-3.5 w-3.5 text-muted-foreground" />
                                <span className="text-xs text-muted-foreground truncate max-w-[150px]">
                                  {activity.customer.nome || 'Cliente'}
                                </span>
                              </div>
                            </div>
                          )}

                          {/* Descrição com expandir/colapsar */}
                          {activity.description && (
                            <div>
                              <div
                                className={cn(
                                  "bg-muted/60 rounded-md px-3 py-2 text-xs text-foreground [&_*]:text-foreground mt-1.5 cursor-pointer",
                                  !expandedDescriptions.has(activity.id) && "line-clamp-2"
                                )}
                                onClick={() => toggleDescription(activity.id)}
                                dangerouslySetInnerHTML={{ __html: activity.description }}
                              />
                              <button
                                type="button"
                                className="text-[11px] text-primary/70 hover:text-primary mt-0.5"
                                onClick={() => toggleDescription(activity.id)}
                              >
                                {expandedDescriptions.has(activity.id) ? 'Ver menos' : 'Ver mais'}
                              </button>
                            </div>
                          )}

                          {/* Anexos */}
                          {activity.attachments && activity.attachments.length > 0 && (
                            <div className="mt-2">
                              <ActivityAttachmentsDisplay
                                attachments={activity.attachments as unknown as Parameters<typeof ActivityAttachmentsDisplay>[0]['attachments']}
                                compact={true}
                              />
                            </div>
                          )}

                          {/* Horário e responsável */}
                          {/* Seção de comentários - oculta para atividades do sistema */}
                          {!activity.is_system && (
                            <div className="mt-2 pt-2 border-t border-border/50">
                              <ActivityCommentSection
                                activityId={activity.id}
                                commentsCount={activity.comments_count || 0}
                                defaultExpanded={commentsDefaultExpanded}
                              />
                            </div>
                          )}

                          <div className="flex items-center gap-2 mt-2">
                            <span className="text-[11px] text-muted-foreground">
                              {format(new Date(activity.created_at), "HH:mm", { locale: ptBR })}
                            </span>

                            {activity.performed_by_user && (
                              <>
                                <span className="text-muted-foreground/40">•</span>
                                <span className="text-[11px] text-muted-foreground">
                                  {activity.performed_by_user.full_name || 'Usuário'}
                                </span>
                              </>
                            )}
                          </div>
                        </div>

                        {/* Avatar + Ações */}
                        <div className="flex flex-col items-end gap-2">
                          {/* Avatar do responsável */}
                          {activity.performed_by_user && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div>
                                  <SmartAvatar
                                    src={activity.performed_by_user.avatar_url}
                                    name={activity.performed_by_user.full_name}
                                    size="sm"
                                    className="border-2 border-background shadow-sm"
                                  />
                                </div>
                              </TooltipTrigger>
                              <TooltipContent side="left" className="text-xs">
                                {activity.performed_by_user.full_name || 'Usuário'}
                              </TooltipContent>
                            </Tooltip>
                          )}

                          {/* Ações (visíveis no hover) */}
                          {(() => {
                            const isNoteOrSystem = activity.is_system || activity.activity_type === 'note';
                            const showCompleteFlow = !activity.is_system && activity.activity_type !== 'note';
                            return (
                              <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                {/* v1.11.0: Marcar como Lido (Notas e Sistema, quando NÃO lida) */}
                                {isNoteOrSystem && !activity.is_read && onMarkRead && (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-6 w-6 text-muted-foreground hover:text-emerald-600 hover:bg-emerald-100 dark:hover:bg-emerald-900/30"
                                        onClick={() => onMarkRead(activity.id)}
                                        disabled={isMarkingRead}
                                      >
                                        <CheckCircle2 className="h-3.5 w-3.5" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent side="top" className="text-xs">
                                      Marcar como lido
                                    </TooltipContent>
                                  </Tooltip>
                                )}

                                {/* v1.11.0: Marcar como NÃO lido (Notas e Sistema, quando lida) */}
                                {isNoteOrSystem && activity.is_read && onMarkUnread && (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-6 w-6 text-muted-foreground hover:text-amber-600 hover:bg-amber-100 dark:hover:bg-amber-900/30"
                                        onClick={() => onMarkUnread(activity.id)}
                                        disabled={isMarkingRead}
                                      >
                                        <RotateCcw className="h-3.5 w-3.5" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent side="top" className="text-xs">
                                      Marcar como não lido
                                    </TooltipContent>
                                  </Tooltip>
                                )}

                                {/* Marcar como concluída (manuais não-nota, pendente) */}
                                {showCompleteFlow && !activity.completed_at && (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-6 w-6 text-muted-foreground hover:text-emerald-600 hover:bg-emerald-100 dark:hover:bg-emerald-900/30"
                                        onClick={() => onComplete(activity.id)}
                                        disabled={isCompleting}
                                      >
                                        <CheckCircle2 className="h-3.5 w-3.5" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent side="top" className="text-xs">
                                      Marcar como concluída
                                    </TooltipContent>
                                  </Tooltip>
                                )}

                                {/* Reabrir atividade (manuais não-nota, concluída) */}
                                {showCompleteFlow && activity.completed_at && (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-6 w-6 text-muted-foreground hover:text-amber-600 hover:bg-amber-100 dark:hover:bg-amber-900/30"
                                        onClick={() => onReopen(activity.id)}
                                        disabled={isReopening}
                                      >
                                        <RotateCcw className="h-3.5 w-3.5" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent side="top" className="text-xs">
                                      Reabrir atividade
                                    </TooltipContent>
                                  </Tooltip>
                                )}

                                {/* Excluir (não disponível para atividades de sistema) */}
                                {!activity.is_system && (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-6 w-6 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                                        onClick={() => onDelete(activity.id)}
                                        disabled={isDeleting}
                                      >
                                        <Trash2 className="h-3.5 w-3.5" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent side="top" className="text-xs">
                                      Excluir
                                    </TooltipContent>
                                  </Tooltip>
                                )}
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </TooltipProvider>
  );
}
